/**
 * Registry of use-case landing pages (`/:lang/use-cases/:slug`).
 *
 * Pure data, no copy: every visible string lives in the `usecases` i18n
 * namespace under `pages.<slug>`. The preset is what the converter is set to
 * when a visitor arrives from the page, and it is also what the page's
 * "Recommended settings" table shows, so the two can never drift apart.
 *
 * `ocr` / `transparentBg` are `undefined` when the option is not offered for
 * the preset's image type (the converter hides the checkbox); a test enforces
 * that a preset never sets an option its image type does not support.
 *
 * Cut, engrave and editor destinations use Design or Silhouette rather than
 * Logo: svgr_api tiles Logo/Signature images and wraps each tile in a
 * `<clipPath>`, which Cricut Design Space does not support.
 *
 * `mergePaths` only has an effect for photo-family types: svgr_api's
 * `getEffectiveMergePaths` forces it off for every design-family type (Logo,
 * Line Art, Signature, Silhouette, ...), so those presets leave it off rather
 * than show a setting that does nothing.
 *
 * The slug list must stay identical to `USE_CASE_SLUGS` in
 * `functions/_middleware.js` and the `use-cases-*` routes in `seo.config.mjs`
 * (checked by `use-cases.test.ts`).
 */

import type { ImageType } from '@sudobility/svgr_lib';

/** Slugs in hub order. */
export const USE_CASE_SLUGS = [
  'qr-code-to-vector',
  'signature-to-vector',
  'logo-to-svg',
  'ai-logo-to-vector',
  'ai-art-to-vector',
  'image-to-svg-for-cricut',
  'svg-for-3d-printing',
  'sketch-to-vector',
  'tattoo-to-vector',
  'vector-for-screen-printing',
  'photo-to-vector-art',
  'svg-for-laser-engraving',
  'png-to-svg-for-canva-figma',
  'svg-for-vinyl-stickers',
] as const;

export type UseCaseSlug = (typeof USE_CASE_SLUGS)[number];

/** Hub sections, in display order. */
export const USE_CASE_GROUPS = ['print', 'makers', 'digital'] as const;
export type UseCaseGroup = (typeof USE_CASE_GROUPS)[number];

/** Converter settings applied when arriving from a use-case page. */
export interface UseCasePreset {
  imageType: ImageType;
  /** 1–10. */
  quality: number;
  /** `undefined` = "Recognize text" is not offered for this image type. */
  ocr?: boolean;
  /** `undefined` = "Transparent background" is not offered for this image type. */
  transparentBg?: boolean;
  mergePaths: boolean;
}

/** A real before/after pair produced by the SVGR engine with the page's preset. */
export interface UseCaseSample {
  /** Raster source, served from `public/`. */
  before: string;
  /** Unedited SVGR output, served from `public/`. */
  after: string;
  /** Intrinsic size of the source image (the SVG has the same aspect ratio). */
  width: number;
  height: number;
  /** Detail strip: centre of the zoomed area (0–1 of width/height) and magnification. */
  zoom: { x: number; y: number; scale: number };
}

export interface UseCaseConfig {
  slug: UseCaseSlug;
  group: UseCaseGroup;
  preset: UseCasePreset;
  /** 3–4 related pages shown at the bottom. */
  related: UseCaseSlug[];
  /** Sibling tutorial under `/:lang/tutorials/<slug>`, cross-linked both ways. */
  tutorialSlug?: string;
  sample: UseCaseSample;
}

const sample = (
  slug: UseCaseSlug,
  width: number,
  height: number,
  zoom: UseCaseSample['zoom']
): UseCaseSample => ({
  before: `/samples/use-cases/${slug}-before.webp`,
  after: `/samples/use-cases/${slug}-after.svg`,
  width,
  height,
  zoom,
});

export const USE_CASES: Record<UseCaseSlug, UseCaseConfig> = {
  'qr-code-to-vector': {
    slug: 'qr-code-to-vector',
    group: 'print',
    // Design · 2, not a higher quality: the speckle filter and lower colour
    // precision at this level drop the anti-aliased halo around each module
    // instead of tracing it. On the 800 px sample, quality 2 gave 94 distinct
    // fills and 55 KB against 642 fills and 131 KB at quality 5, and decoded at
    // every render width. The trade-off is a module-size floor, which is why
    // the page tells users to upload at least ~800 px across.
    preset: {
      imageType: 'design',
      quality: 2,
      ocr: false,
      transparentBg: false,
      mergePaths: false,
    },
    related: [
      'logo-to-svg',
      'signature-to-vector',
      'vector-for-screen-printing',
      'svg-for-vinyl-stickers',
    ],
    tutorialSlug: 'qr-code-to-svg',
    sample: sample('qr-code-to-vector', 400, 439, { x: 0.14, y: 0.14, scale: 3 }),
  },
  'signature-to-vector': {
    slug: 'signature-to-vector',
    group: 'print',
    // Quality 2: the pen stroke on paper has a soft anti-aliased edge and
    // paper grain, and a higher setting traces both as pale ghost shapes
    // around the ink. The low speckle filter drops them (48 fills instead of
    // 743 at quality 6) while thin strokes survive.
    preset: { imageType: 'signature', quality: 2, mergePaths: false },
    related: ['logo-to-svg', 'sketch-to-vector', 'svg-for-laser-engraving', 'qr-code-to-vector'],
    sample: sample('signature-to-vector', 900, 360, { x: 0.17, y: 0.55, scale: 6 }),
  },
  'logo-to-svg': {
    slug: 'logo-to-svg',
    group: 'print',
    // Quality 2 strips the JPEG fringe around flat color areas: 46 fills
    // instead of 424 at quality 6, with the same shapes.
    preset: {
      imageType: 'logo',
      quality: 2,
      ocr: false,
      transparentBg: true,
      mergePaths: false,
    },
    related: [
      'ai-logo-to-vector',
      'vector-for-screen-printing',
      'png-to-svg-for-canva-figma',
      'qr-code-to-vector',
    ],
    sample: sample('logo-to-svg', 720, 360, { x: 0.55, y: 0.5, scale: 5 }),
  },
  'ai-logo-to-vector': {
    slug: 'ai-logo-to-vector',
    group: 'print',
    // Infographic, not Logo: design-family types only fit gradients when
    // color refinement runs, which Logo does only when OCR finds text;
    // flat_infographic always refines, so AI-logo gradients survive.
    preset: {
      imageType: 'flat_infographic',
      quality: 7,
      ocr: false,
      transparentBg: true,
      mergePaths: false,
    },
    related: [
      'logo-to-svg',
      'ai-art-to-vector',
      'png-to-svg-for-canva-figma',
      'image-to-svg-for-cricut',
    ],
    tutorialSlug: 'ai-logos-to-svg',
    sample: sample('ai-logo-to-vector', 1024, 1024, { x: 0.3, y: 0.45, scale: 7 }),
  },
  'ai-art-to-vector': {
    slug: 'ai-art-to-vector',
    group: 'digital',
    preset: { imageType: 'illustration', quality: 6, transparentBg: false, mergePaths: false },
    related: [
      'ai-logo-to-vector',
      'sketch-to-vector',
      'photo-to-vector-art',
      'vector-for-screen-printing',
    ],
    tutorialSlug: 'ai-posters-to-svg',
    sample: sample('ai-art-to-vector', 640, 960, { x: 0.5, y: 0.2, scale: 4 }),
  },
  'image-to-svg-for-cricut': {
    slug: 'image-to-svg-for-cricut',
    group: 'makers',
    preset: { imageType: 'design', quality: 4, ocr: false, transparentBg: true, mergePaths: false },
    related: [
      'svg-for-vinyl-stickers',
      'svg-for-laser-engraving',
      'svg-for-3d-printing',
      'logo-to-svg',
    ],
    sample: sample('image-to-svg-for-cricut', 600, 600, { x: 0.4, y: 0.73, scale: 4 }),
  },
  'svg-for-3d-printing': {
    slug: 'svg-for-3d-printing',
    group: 'makers',
    preset: { imageType: 'silhouette_cutout', quality: 4, transparentBg: true, mergePaths: false },
    related: [
      'image-to-svg-for-cricut',
      'svg-for-laser-engraving',
      'svg-for-vinyl-stickers',
      'logo-to-svg',
    ],
    sample: sample('svg-for-3d-printing', 640, 320, { x: 0.35, y: 0.5, scale: 4 }),
  },
  'sketch-to-vector': {
    slug: 'sketch-to-vector',
    group: 'digital',
    // Quality 2: pencil and scan grain around every line becomes ghost
    // shapes at higher settings (435 fills at quality 6 against 44 here);
    // whiskers and other thin strokes still come through.
    preset: {
      imageType: 'line_art',
      quality: 2,
      ocr: false,
      transparentBg: true,
      mergePaths: false,
    },
    related: [
      'tattoo-to-vector',
      'ai-art-to-vector',
      'image-to-svg-for-cricut',
      'signature-to-vector',
    ],
    sample: sample('sketch-to-vector', 800, 800, { x: 0.38, y: 0.42, scale: 5 }),
  },
  'tattoo-to-vector': {
    slug: 'tattoo-to-vector',
    group: 'digital',
    // Quality 2 keeps the linework hard-edged: 40 fills instead of 609 at
    // quality 6, which traced the scan's grain as a halo around every line.
    preset: {
      imageType: 'tattoo_flash',
      quality: 2,
      transparentBg: true,
      mergePaths: false,
    },
    related: [
      'sketch-to-vector',
      'vector-for-screen-printing',
      'signature-to-vector',
      'svg-for-vinyl-stickers',
    ],
    sample: sample('tattoo-to-vector', 800, 800, { x: 0.5, y: 0.6, scale: 5 }),
  },
  'vector-for-screen-printing': {
    slug: 'vector-for-screen-printing',
    group: 'print',
    preset: { imageType: 'design', quality: 4, ocr: false, transparentBg: true, mergePaths: false },
    related: ['logo-to-svg', 'svg-for-vinyl-stickers', 'tattoo-to-vector', 'ai-art-to-vector'],
    sample: sample('vector-for-screen-printing', 700, 700, { x: 0.58, y: 0.93, scale: 5 }),
  },
  'photo-to-vector-art': {
    slug: 'photo-to-vector-art',
    group: 'digital',
    preset: { imageType: 'photo', quality: 5, mergePaths: true },
    related: [
      'ai-art-to-vector',
      'sketch-to-vector',
      'svg-for-laser-engraving',
      'png-to-svg-for-canva-figma',
    ],
    tutorialSlug: 'photo-to-svg',
    sample: sample('photo-to-vector-art', 640, 853, { x: 0.55, y: 0.5, scale: 4 }),
  },
  'svg-for-laser-engraving': {
    slug: 'svg-for-laser-engraving',
    group: 'makers',
    preset: { imageType: 'silhouette_cutout', quality: 4, transparentBg: true, mergePaths: false },
    related: [
      'image-to-svg-for-cricut',
      'svg-for-3d-printing',
      'signature-to-vector',
      'svg-for-vinyl-stickers',
    ],
    sample: sample('svg-for-laser-engraving', 600, 600, { x: 0.5, y: 0.52, scale: 4 }),
  },
  'png-to-svg-for-canva-figma': {
    slug: 'png-to-svg-for-canva-figma',
    group: 'digital',
    preset: { imageType: 'design', quality: 5, ocr: false, transparentBg: true, mergePaths: false },
    related: ['logo-to-svg', 'ai-logo-to-vector', 'sketch-to-vector', 'photo-to-vector-art'],
    sample: sample('png-to-svg-for-canva-figma', 512, 512, { x: 0.5, y: 0.42, scale: 3 }),
  },
  'svg-for-vinyl-stickers': {
    slug: 'svg-for-vinyl-stickers',
    group: 'makers',
    preset: { imageType: 'silhouette_cutout', quality: 3, transparentBg: true, mergePaths: false },
    related: [
      'image-to-svg-for-cricut',
      'svg-for-laser-engraving',
      'vector-for-screen-printing',
      'svg-for-3d-printing',
    ],
    sample: sample('svg-for-vinyl-stickers', 800, 300, { x: 0.2, y: 0.5, scale: 5 }),
  },
};

/** Pages shown in the home footer's "Use cases" column. */
export const FOOTER_USE_CASES: readonly UseCaseSlug[] = USE_CASE_SLUGS.slice(0, 6);

export function isUseCaseSlug(value: string | null | undefined): value is UseCaseSlug {
  return !!value && (USE_CASE_SLUGS as readonly string[]).includes(value);
}
